/* Site-wide sticky cart rewards bar — keeps the two-step ladder
   (free shipping -> -20% at 3 items) in sync with the live cart.
   The cart-drawer renders the same ladder server-side; this only
   drives the standalone <cart-reward-bar>. */
(function () {
  if (customElements.get('cart-reward-bar')) return;

  class CartRewardBar extends HTMLElement {
    connectedCallback() {
      this.minimum = parseInt(this.dataset.minimum, 10) || 0;
      this.threshold = parseInt(this.dataset.threshold, 10) || 3;
      this.tpl = {
        shipping: this.dataset.tplShipping || '',
        discount: this.dataset.tplDiscount || '',
        discountPlural: this.dataset.tplDiscountPlural || '',
        complete: this.dataset.tplComplete || ''
      };

      this.ladder = this.querySelector('.cart-reward-ladder');
      this.messageEl = this.querySelector('.cart-reward-ladder--message');
      this.trackEl = this.querySelector('.cart-reward-ladder--track');
      this.node1 = this.querySelector('.cart-reward-ladder--node.is-1');
      this.node2 = this.querySelector('.cart-reward-ladder--node.is-2');

      // Initial paint from the server-rendered data attributes.
      this.update(parseInt(this.dataset.cartTotal, 10) || 0, parseInt(this.dataset.itemCount, 10) || 0);

      // Clicking (or Enter/Space on) the whole bar reopens the cart drawer.
      this._openDrawer = () => {
        var drawer = document.getElementById('Cart-Drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      };
      this.addEventListener('click', (e) => {
        // Stop the document-level "click outside -> close" handler in the
        // cart drawer from immediately closing what we just opened.
        e.stopPropagation();
        this._openDrawer();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          e.stopPropagation();
          this._openDrawer();
        }
      });

      this._onChangeEnd = (e) => {
        var cart = e.detail && e.detail.cart;
        if (cart && typeof cart.total_price !== 'undefined') {
          // This event already carries the new totals — no extra request.
          this.update(cart.total_price, cart.item_count);
        } else {
          this.scheduleFetch();
        }
      };
      // On add/refresh the theme fires concurrent /cart/update.js writes; wait
      // for that burst to settle before our read so we don't add to the storm.
      this._onCartEvent = () => this.scheduleFetch();

      document.addEventListener('line-item:change:end', this._onChangeEnd);
      document.addEventListener('cart:item-added', this._onCartEvent);
      document.addEventListener('cart:refresh', this._onCartEvent);
    }

    disconnectedCallback() {
      clearTimeout(this._fetchTimer);
      document.removeEventListener('line-item:change:end', this._onChangeEnd);
      document.removeEventListener('cart:item-added', this._onCartEvent);
      document.removeEventListener('cart:refresh', this._onCartEvent);
    }

    scheduleFetch() {
      clearTimeout(this._fetchTimer);
      this._fetchTimer = setTimeout(() => this.fetchCart(), 700);
    }

    fetchCart() {
      if (this._fetching) return;
      this._fetching = true;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => this.update(cart.total_price, cart.item_count))
        .catch(() => {})
        .finally(() => { this._fetching = false; });
    }

    update(total, itemCount) {
      total = parseInt(total, 10) || 0;
      itemCount = parseInt(itemCount, 10) || 0;

      // Hide entirely on an empty cart.
      if (itemCount < 1) {
        this.hidden = true;
        document.body.classList.remove('has-cart-reward-bar');
        return;
      }
      this.hidden = false;
      document.body.classList.add('has-cart-reward-bar');

      var rate = (window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
      var minimum = Math.round(this.minimum * rate);
      var shippingReached = minimum > 0 ? total >= minimum : true;
      var discountReached = itemCount >= this.threshold;

      var progress;
      var state;
      if (!shippingReached) {
        progress = minimum > 0 ? (total / minimum) * 0.5 : 0;
        state = 'need-shipping';
      } else if (!discountReached) {
        progress = 0.5 + (itemCount / this.threshold) * 0.5;
        state = 'need-discount';
      } else {
        progress = 1;
        state = 'complete';
      }
      if (progress > 1) progress = 1;
      if (progress < 0) progress = 0;

      if (this.ladder) this.ladder.dataset.state = state;
      if (this.trackEl) this.trackEl.style.setProperty('--reward-progress', progress);
      if (this.node1) this.node1.setAttribute('data-reached', shippingReached ? 'true' : 'false');
      if (this.node2) this.node2.setAttribute('data-reached', discountReached ? 'true' : 'false');

      if (this.messageEl) {
        if (state === 'need-shipping') {
          var format = (window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) || '${{amount}}';
          var remaining = minimum - total;
          var amount = (typeof formatMoney === 'function') ? formatMoney(remaining, format) : remaining;
          this.messageEl.innerHTML = this.tpl.shipping.replace('%amt%', amount);
        } else if (state === 'need-discount') {
          var left = this.threshold - itemCount;
          if (left < 1) left = 1;
          var tpl = left === 1 ? this.tpl.discount : this.tpl.discountPlural;
          this.messageEl.textContent = tpl.replace('%n%', left);
        } else {
          this.messageEl.textContent = this.tpl.complete;
        }
      }

      // Expose the bar height so the newsletter banner / sticky add-to-cart
      // can be lifted above it (see cart-reward-ladder.css).
      var self = this;
      requestAnimationFrame(function () {
        document.body.style.setProperty('--bm-cart-bar-h', self.offsetHeight + 'px');
      });
    }
  }

  customElements.define('cart-reward-bar', CartRewardBar);
})();
